<template>
    <Card pageTitle="Customer Loyalty">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_LOYALTY">
            <div class="loyalty-wrap">
                <div class="page-head">
                    <h2>Customer Loyalty</h2>
                    <p>View balance, tier, manual adjustments, redemptions, and the append-only point ledger.</p>
                    <div v-if="customerReady" class="customer-identity">
                        <strong>{{ customerName }}</strong>
                        <span>Customer ID: {{ account.customer.id }}</span>
                    </div>
                    <p v-else role="status">{{ loading ? "Loading customer identity…" : "Customer identity unavailable. Reload this page before editing points." }}</p>
                </div>

                <div class="actions">
                    <Button borderOnly @click="$router.back()">Back</Button>
                    <Button v-if="canEdit" borderOnly @click="syncBalance" :disabled="syncing || adjusting || !customerReady">
                        {{ syncing ? "Recalculating..." : "Recalculate local balance" }}
                    </Button>
                </div>
                <p class="balance-help">Recalculates the cached local balance from available, unexpired point lots. This does not synchronize with Cova or SpringBig.</p>
                <p v-if="recalculationResult" class="balance-result" role="status">{{ recalculationResult }}</p>

                <div class="summary">
                    <div class="stat">
                        <div class="stat-label">Points balance</div>
                        <div class="stat-value">{{ account.points_balance || 0 }}</div>
                    </div>
                    <div class="stat">
                        <div class="stat-label">Tier</div>
                        <div class="stat-value">{{ account.current_tier || "—" }}</div>
                    </div>
                    <div class="stat">
                        <div class="stat-label">Lifetime visits</div>
                        <div class="stat-value">{{ account.lifetime_visits || 0 }}</div>
                    </div>
                    <div class="stat">
                        <div class="stat-label">Lifetime points</div>
                        <div class="stat-value">{{ account.lifetime_points || 0 }}</div>
                    </div>
                </div>

                <div v-if="canAdjust" class="adjust-box">
                    <h3>Manual adjustment<span v-if="customerReady"> for {{ customerName }}</span></h3>
                    <div class="grid">
                        <CustomInput type="number" :step="1" placeholder="Points (+/-)" v-model="adjust.points" :disabled="adjusting || syncing || !customerReady" />
                        <CustomInput placeholder="Reason (required)" v-model="adjust.reason" :disabled="adjusting || syncing || !customerReady" />
                    </div>
                    <div v-if="adjustPreview" :class="['adjust-preview', adjustPreview.isDeduction ? 'deduct' : 'grant']">
                        <strong>{{ adjustPreview.isDeduction ? "Deduction preview" : "Grant preview" }}</strong>
                        <span>
                            Current {{ account.points_balance || 0 }} pts →
                            {{ adjustPreview.resultingBalance }} pts
                        </span>
                    </div>
                    <Button @click="submitAdjust" :disabled="adjusting || syncing || !customerReady">
                        {{ adjusting ? "Applying..." : "Apply adjustment" }}
                    </Button>
                </div>

                <div class="explain-box">
                    <div class="section-head-inline">
                        <h3>Earn visibility</h3>
                        <span :class="['status-badge', earningExplanation.currently_eligible ? 'positive-tone' : 'neutral-tone']">
                            {{ earningExplanation.currently_eligible ? "Eligible now" : "Not eligible now" }}
                        </span>
                    </div>
                    <div class="explain-grid">
                        <div>
                            <span class="stat-label">Last earned</span>
                            <strong>{{ formatDate(earningExplanation.last_earned_at) }}</strong>
                        </div>
                        <div>
                            <span class="stat-label">Next eligible</span>
                            <strong>{{ formatDate(earningExplanation.next_eligible_at) }}</strong>
                        </div>
                        <div>
                            <span class="stat-label">Points per visit</span>
                            <strong>{{ earningExplanation.points_per_visit || 0 }}</strong>
                        </div>
                    </div>
                    <ul class="reason-list">
                        <li v-for="reason in earningExplanation.reasons || []" :key="reason">{{ reason }}</li>
                    </ul>
                </div>

                <h3 class="section-title">Ledger</h3>
                <div class="ledger-filters">
                    <select v-model="ledgerFilters.type" class="native-select" @change="loadLedger">
                        <option value="">All types</option>
                        <option value="EARN">Earn</option>
                        <option value="REDEEM">Redeem</option>
                        <option value="CLAWBACK">Clawback</option>
                        <option value="ADJUST_GRANT">Manual grant</option>
                        <option value="ADJUST_DEDUCT">Manual deduct</option>
                        <option value="EXPIRE">Expire</option>
                    </select>
                    <CustomInput placeholder="Search reason/ref/by" v-model="ledgerFilters.search" @keyup.native="debouncedLedgerLoad" />
                    <CustomInput type="date" v-model="ledgerFilters.start_date" @change.native="loadLedger" />
                    <CustomInput type="date" v-model="ledgerFilters.end_date" @change.native="loadLedger" />
                    <Button borderOnly @click="exportLedger" :disabled="exporting">{{ exporting ? "Exporting..." : "Export CSV" }}</Button>
                </div>
                <table class="ledger-table" v-if="ledger.length">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Points</th>
                            <th>Balance after</th>
                            <th>Reason / Ref</th>
                            <th>By</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in ledger" :key="row.id">
                            <td>{{ formatDate(row.createdAt) }}</td>
                            <td><span :class="['type-badge', transactionTone(row.type)]">{{ row.type }}</span></td>
                            <td :class="{ positive: row.points_delta > 0, negative: row.points_delta < 0 }">
                                {{ row.points_delta > 0 ? "+" : "" }}{{ row.points_delta }}
                            </td>
                            <td>{{ row.balance_after }}</td>
                            <td>{{ row.reason || row.invoice_name || row.invoice_id || "—" }}</td>
                            <td>{{ row.performed_by_name || "—" }}</td>
                        </tr>
                    </tbody>
                </table>
                <p v-else class="empty">No loyalty activity yet.</p>

                <h3 class="section-title redemptions-title">Redemptions</h3>
                <table class="ledger-table" v-if="redemptions.length">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Reward</th>
                            <th>Item</th>
                            <th>Points</th>
                            <th>Status</th>
                            <th>Token</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="redemption in redemptions" :key="redemption.id">
                            <td>{{ formatDate(redemption.createdAt) }}</td>
                            <td>{{ redemption.reward_name || "—" }}</td>
                            <td>{{ redemption.item_code || "—" }}</td>
                            <td>{{ redemption.points_spent }}</td>
                            <td>
                                <span :class="['type-badge', redemption.status === 'VOIDED' ? 'negative-tone' : 'neutral-tone']">
                                    {{ redemption.status || "—" }}
                                </span>
                            </td>
                            <td class="token-cell">{{ redemption.token || "—" }}</td>
                        </tr>
                    </tbody>
                </table>
                <p v-else class="empty">No redemptions yet.</p>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { confirmDialog, errMsg } from "../../utils/common"

export default {
    components: { Card, Button, CustomInput, AllowPermission },
    data() {
        return {
            PERMISSION_CODES,
            account: {},
            loading: false,
            routeVersion: 0,
            loadRequestId: 0,
            ledgerRequestId: 0,
            disposed: false,
            recalculationResult: "",
            ledger: [],
            redemptions: [],
            adjusting: false,
            syncing: false,
            exporting: false,
            ledgerFilters: { type: "", search: "", start_date: "", end_date: "" },
            ledgerTimer: null,
            adjust: { points: 0, reason: "" },
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        customerId() {
            return this.$route.params.id
        },
        customerReady() {
            return !this.loading && String(this.account.customer?.id || "") === String(this.customerId)
        },
        customerName() {
            return this.account.customer?.customer_name?.trim() || "Unnamed customer"
        },
        customerLabel() {
            return `${this.customerName} (Customer ID: ${this.account.customer.id})`
        },
        canAdjust() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_ADJUST_LOYALTY_POINTS,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_LOYALTY,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        earningExplanation() {
            return this.account?.earning_explanation || { reasons: [] }
        },
        adjustPreview() {
            const points = Math.round(Number(this.adjust.points))
            if (!Number.isFinite(points) || points === 0) return null
            return {
                isDeduction: points < 0,
                resultingBalance: Math.max(0, Number(this.account.points_balance || 0) + points),
            }
        },
    },
    async mounted() {
        await this.loadAll()
    },
    watch: {
        customerId() {
            this.routeVersion += 1
            clearTimeout(this.ledgerTimer)
            this.adjust = { points: 0, reason: "" }
            this.adjusting = false
            this.syncing = false
            this.exporting = false
            this.recalculationResult = ""
            this.loadAll()
        },
    },
    beforeDestroy() {
        this.disposed = true
        clearTimeout(this.ledgerTimer)
    },
    methods: {
        isCurrentCustomer(customerId, routeVersion) {
            return !this.disposed && String(customerId) === String(this.customerId) && routeVersion === this.routeVersion
        },
        async loadAll() {
            const customerId = this.customerId
            const routeVersion = this.routeVersion
            const requestId = ++this.loadRequestId
            const isCurrent = () => this.isCurrentCustomer(customerId, routeVersion) && requestId === this.loadRequestId
            this.loading = true
            this.account = {}
            this.ledger = []
            this.redemptions = []
            // History has independent availability; only the account identifies the editor.
            await Promise.all([
                (async () => {
                    try {
                        const account = await LoyaltyFetchApi.fetchCustomerLoyalty(customerId)
                        if (isCurrent()) this.account = account || {}
                    } catch (err) {
                        if (isCurrent()) this.$toast.error(errMsg(err, "Could not load loyalty data"))
                    } finally {
                        if (isCurrent()) this.loading = false
                    }
                })(),
                this.loadLedger(),
                (async () => {
                    try {
                        const redemptionsRes = await LoyaltyFetchApi.fetchCustomerRedemptions(customerId)
                        if (isCurrent()) this.redemptions = redemptionsRes?.result || []
                    } catch (err) {
                        if (isCurrent()) this.$toast.error(errMsg(err, "Could not load loyalty redemptions"))
                    }
                })(),
            ])
        },
        async loadLedger() {
            const customerId = this.customerId
            const routeVersion = this.routeVersion
            const requestId = ++this.ledgerRequestId
            try {
                const ledgerRes = await LoyaltyFetchApi.fetchCustomerLedger(customerId, 1, 50, { ...this.ledgerFilters })
                if (this.isCurrentCustomer(customerId, routeVersion) && requestId === this.ledgerRequestId) this.ledger = ledgerRes?.result || []
            } catch (err) {
                if (this.isCurrentCustomer(customerId, routeVersion) && requestId === this.ledgerRequestId) this.$toast.error(errMsg(err, "Could not load loyalty ledger"))
            }
        },
        debouncedLedgerLoad() {
            clearTimeout(this.ledgerTimer)
            this.ledgerTimer = setTimeout(() => this.loadLedger(), 300)
        },
        async exportLedger() {
            const customerId = this.customerId
            const routeVersion = this.routeVersion
            this.exporting = true
            try {
                const blob = await LoyaltyFetchApi.exportCustomerLedger(customerId, { ...this.ledgerFilters })
                if (!this.isCurrentCustomer(customerId, routeVersion)) return
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = url
                link.download = `loyalty-ledger-${customerId}.csv`
                link.click()
                window.URL.revokeObjectURL(url)
            } catch (err) {
                if (this.isCurrentCustomer(customerId, routeVersion)) this.$toast.error(errMsg(err, "Could not export ledger"))
            } finally {
                if (this.isCurrentCustomer(customerId, routeVersion)) this.exporting = false
            }
        },
        async submitAdjust() {
            if (!this.canAdjust || !this.customerReady || this.adjusting || this.syncing) return
            const customerId = this.customerId
            const routeVersion = this.routeVersion
            const customerLabel = this.customerLabel
            const points = Math.round(Number(this.adjust.points))
            if (!Number.isFinite(points) || points === 0) {
                this.$toast.error("Enter a non-zero points value")
                return
            }
            if (!this.adjust.reason || !this.adjust.reason.trim()) {
                this.$toast.error("A reason is required")
                return
            }
            const payload = { points, reason: this.adjust.reason.trim() }
            const currentBalance = Number(this.account.points_balance || 0)
            const nextBalance = currentBalance + points
            if (points < 0) {
                if (nextBalance < 0) {
                    this.$toast.error("Deduction cannot exceed the current balance")
                    return
                }
                payload.confirm_deduction = true
            }
            this.adjusting = true
            try {
                const confirmed = await confirmDialog({
                    title: points < 0 ? "Confirm loyalty deduction" : "Confirm loyalty grant",
                    text: `${customerLabel}. ${points < 0 ? "Deduct" : "Grant"} ${Math.abs(points)} points. Current balance: ${currentBalance}. Resulting balance: ${nextBalance}.`,
                    confirmButtonText: points < 0 ? "Deduct points" : "Grant points",
                })
                if (!confirmed || !this.isCurrentCustomer(customerId, routeVersion) || !this.customerReady || !this.canAdjust) return
                await LoyaltyFetchApi.adjustPoints(customerId, payload)
                if (!this.isCurrentCustomer(customerId, routeVersion)) return
                this.adjust = { points: 0, reason: "" }
                this.recalculationResult = ""
                await this.loadAll()
                if (this.isCurrentCustomer(customerId, routeVersion)) this.$toast.success(`Adjustment applied for ${customerLabel}`)
            } catch (err) {
                if (this.isCurrentCustomer(customerId, routeVersion)) this.$toast.error(errMsg(err, "Could not apply adjustment"))
            } finally {
                if (this.isCurrentCustomer(customerId, routeVersion)) this.adjusting = false
            }
        },
        async syncBalance() {
            if (!this.canEdit || !this.customerReady || this.syncing || this.adjusting) return
            const customerId = this.customerId
            const routeVersion = this.routeVersion
            const customerLabel = this.customerLabel
            this.syncing = true
            this.recalculationResult = ""
            try {
                const result = await LoyaltyFetchApi.syncCustomerBalance(customerId)
                if (!this.isCurrentCustomer(customerId, routeVersion)) return
                const outcome = result?.balance_recalculation
                this.recalculationResult = outcome
                    ? `Local balance recalculated for ${customerLabel}: ${outcome.previous_balance} → ${outcome.new_balance} points.`
                    : `Local balance recalculated for ${customerLabel}. Reloaded the current balance.`
                await this.loadAll()
                if (this.isCurrentCustomer(customerId, routeVersion)) this.$toast.success(this.recalculationResult)
            } catch (err) {
                if (this.isCurrentCustomer(customerId, routeVersion)) this.$toast.error(errMsg(err, "Could not recalculate local balance"))
            } finally {
                if (this.isCurrentCustomer(customerId, routeVersion)) this.syncing = false
            }
        },
        formatDate(d) {
            if (!d) return "—"
            return new Date(d).toLocaleString()
        },
        transactionTone(type) {
            const value = String(type || "").toLowerCase()
            if (value.includes("earn") || value.includes("grant")) return "positive-tone"
            if (value.includes("redeem") || value.includes("deduct") || value.includes("clawback") || value.includes("expire")) return "negative-tone"
            return "neutral-tone"
        },
    },
}
</script>

<style lang="scss" scoped>
.loyalty-wrap { max-width: 900px; }
.page-head { margin-bottom: 18px; }
.page-head h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: #0b302c; }
.page-head p { margin: 0; color: #5f6368; line-height: 1.45; }
.customer-identity { display: flex; flex-direction: column; gap: 4px; margin-top: 12px; overflow-wrap: anywhere; }
.customer-identity strong { font-size: 20px; color: #0b302c; }
.customer-identity span, .balance-help { color: #5f6368; }
.balance-help { margin-bottom: 12px; line-height: 1.45; }
.balance-result { padding: 12px; background: #e8f5ec; border-radius: 8px; margin-bottom: 16px; overflow-wrap: anywhere; }
.actions { display: flex; gap: 8px; margin-bottom: 16px; }
.summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
.stat { border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px; }
.stat-label { color: #80868b; font-size: 13px; }
.stat-value { font-size: 22px; font-weight: 700; }
.adjust-box { border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px; margin-bottom: 24px; }
.adjust-box h3 { font-weight: 600; margin-bottom: 12px; }
.grid { display: grid; grid-template-columns: 1fr 2fr; gap: 12px; margin-bottom: 12px; }
.adjust-preview { display: flex; justify-content: space-between; gap: 12px; border-radius: 8px; padding: 10px 12px; margin-bottom: 12px; font-size: 13px; }
.adjust-preview.grant { background: #e8f5ec; color: #1f7a3b; }
.adjust-preview.deduct { background: #fff3d6; color: #8a4b00; }
.explain-box { border: 1px solid #d5e4e1; background: #f7fbfa; border-radius: 8px; padding: 16px; margin-bottom: 24px; }
.section-head-inline { display: flex; justify-content: space-between; gap: 12px; align-items: center; margin-bottom: 12px; }
.section-head-inline h3 { margin: 0; font-weight: 700; color: #0b302c; }
.explain-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 10px; }
.explain-grid > div { border: 1px solid #e4ecea; border-radius: 8px; padding: 10px; background: #fff; }
.explain-grid strong { display: block; margin-top: 4px; color: #111827; }
.reason-list { margin: 0; padding-left: 18px; color: #4b5563; }
.ledger-filters { display: grid; grid-template-columns: 150px 1fr 150px 150px 120px; gap: 8px; align-items: center; margin-bottom: 12px; }
.native-select { width: 100%; min-height: 40px; border: 1px solid #d9dee3; border-radius: 6px; padding: 8px; background: #fff; }
.section-title { font-weight: 600; margin-bottom: 12px; }
.redemptions-title { margin-top: 28px; }
.ledger-table { width: 100%; border-collapse: collapse; }
.ledger-table th, .ledger-table td { text-align: left; padding: 8px; border-bottom: 1px solid #eee; vertical-align: top; }
.token-cell { font-family: monospace; font-size: 12px; overflow-wrap: anywhere; }
.positive { color: #1a7f37; }
.negative { color: #c5221f; }
.type-badge { display: inline-flex; align-items: center; border-radius: 999px; padding: 3px 8px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.positive-tone { background: #e8f5ec; color: #1f7a3b; }
.negative-tone { background: #feecec; color: #b42318; }
.neutral-tone { background: #eef0f2; color: #4b5563; }
.empty { color: #80868b; }
@media (max-width: 900px) {
    .summary, .explain-grid, .ledger-filters { grid-template-columns: 1fr; }
    .grid { grid-template-columns: 1fr; }
}
</style>
